import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import I18n from 'i18n-js';
import { combineEpics, ofType } from 'redux-observable';
import { EMPTY, forkJoin, of } from 'rxjs';
import { mergeMap, switchMap } from 'rxjs/operators';
import { getToken } from 'grab-login/lib/helper';
import Storage from 'grab-storage';
import { saveCityStorage } from 'helper/storage';
import { isLoginPage, isUserAuthenticatedAsync } from 'helper/auth-helper';
import {
  getDefaultCityForCorpAccount,
  getTempSavedUserProfile,
  clearTempUserProfile,
  isUsingPaxPersonalPayments,
  getUserCountryCode,
  getDefaultCityForPaxUserAccount,
} from 'helper/user';
import {
  FETCH_CITY,
  fetchCityOptions,
  GLOBAL_INIT_CLIENT,
  pushErrorModalAction,
  resetCompanyAndGroup,
  paxFetchBusinessProfilesAction,
  PAX_FETCH_BUSINESS_PROFILES,
  GLOBAL_INIT_CLIENT_ERROR,
  fetchPaxConfigurationAction,
  globalInitFailureAction,
  FETCH_INITIAL_USER_CONFIGURATION,
  fetchInitialUserConfigurationAction,
} from 'actions/app-actions';
import {
  fetchServiceTypesActionV2,
  getAllPaymentMethodsV2,
  getAllPaxPaymentMethodsV2,
  paxFetchServiceTypesAction,
  FETCH_SERVICE_TYPES_V2,
  PAX_FETCH_SERVICE_TYPES,
} from 'actions/booking-actions';
import { STORAGE_USER_COUNTRY_CODE } from 'constants/app-constants';
import { ERROR_MODAL_TYPE } from 'components/v2/error-modal/error-modal';
import { logOut } from 'helper/logout-helper';
import {
  isPaxUser,
  getPaxUserInfo,
  clearPaxPIIData,
  isGrabIDAuthenticated,
} from 'helper/grab-id-auth-helper';
import {
  userInfoSelector,
  enableGetUserProfilesEndpointSelector,
  enableGetPermissionsEndpointSelector,
} from 'selectors/app-selector';
import { isPaxNotWhitelisted } from 'helper/get-user-feature-flags-helper';
import {
  doAddressSearch,
  extractCityFromSearchResults,
  getInitialCompanyAndGroup,
  getInitAddressSearchParams,
  createPaxLinkPreConditionErrorModal,
} from './epic-helpers';
import { isAuthHintEmpty } from '../../helper/session-helper';

import {
  GET_USERNAME_INFO,
  FETCH_USER_INFO,
  INIT_GFB_USER,
  fetchUserInfoAction,
  getUserAuthorizationAction as fetchUserAuthorizationAction,
  getUserNameAction as fetchUserNameAction,
  getPaxUserInfoAction,
  paxGetUserAuthSuccessAction,
  initGFBUserAction,
  getCompanyPermissionsAction,
  getCompanyUserProfilesAction,
} from '../../actions/login-actions';

const globalInitClientEpic = (action$, _state$) =>
  action$.pipe(
    ofType(GLOBAL_INIT_CLIENT),
    switchMap(async () => await isUserAuthenticatedAsync()),
    switchMap(isUserAuthenticated => {
      /*
       * For GFB users, Do not perform init client unless
       *                already get companies from [getUserFeatureFlagsAsync] in index.js
       * For pax users, they might not have a GFB profile hence no company/ group.
       *                Perform init client if already authenticated
      */
      if (isLoginPage() || !isUserAuthenticated) {
        return EMPTY;
      }

      if (isPaxNotWhitelisted()) {
        return of(
          pushErrorModalAction({
            message: I18n.t('v2.error.home.paxNotWhitelisted.title'),
            description: I18n.t('v2.error.home.paxNotWhitelisted.description'),
            okButtonText: I18n.t('v2.error.home.paxNotWhitelisted.button'),
            type: ERROR_MODAL_TYPE.WARNING,
            onOk: () => {
              window.location.href = window.Grab.config.uri;
            },
          }),
        );
      }

      const cachedIsPaxUser = isGrabIDAuthenticated();
      const isPaxUserWithoutConfig = cachedIsPaxUser && !getPaxUserInfo();
      if (isPaxUserWithoutConfig) {
        return of(globalInitFailureAction());
      }

      // For PAX account login
      if (cachedIsPaxUser) {
        const paxUserInfo = getPaxUserInfo();
        const countryCode = getUserCountryCode();

        const paxActions = [
          getPaxUserInfoAction({
            user: { ...paxUserInfo, safeID: paxUserInfo.sub },
          }),
          paxFetchBusinessProfilesAction(countryCode),
          fetchCityOptions(countryCode),
          getAllPaxPaymentMethodsV2(countryCode),
          paxGetUserAuthSuccessAction(),
          fetchPaxConfigurationAction(countryCode),
        ];

        clearPaxPIIData();
        return paxActions;
      }

      // For GFB account login
      return of(initGFBUserAction());
    }),
  );

// For GFB account login
const initGFBUserConfigsEpic = (action$, _state$) =>
  action$.pipe(
    ofType(INIT_GFB_USER.START),
    switchMap(action => {
      const { company, group } = getInitialCompanyAndGroup();

      // show error if no available company & group
      if (_isEmpty(company) || _isEmpty(group)) {
        return of(
          pushErrorModalAction({
            message: I18n.t('v2.error.home.accountError.title'),
            description: I18n.t('v2.error.home.accountError.description'),
            type: ERROR_MODAL_TYPE.WARNING,
            onOk: () => logOut(),
          }),
        );
      }

      // set country code in local storage
      Storage.setItem(STORAGE_USER_COUNTRY_CODE, company.countryCode);

      const token = getToken();

      // fetch the initial user configuration and user info
      return [
        fetchUserNameAction(token), // user info
        fetchInitialUserConfigurationAction(company.id),
      ];
    }),
  );

const initGFBUserRemainingActionsEpic = (action$, _state$) =>
  action$.pipe(
    ofType(FETCH_INITIAL_USER_CONFIGURATION.SUCCESS),
    switchMap(action => {
      // check feature flags
      const enabledGetUserProfiles = enableGetUserProfilesEndpointSelector(
        _state$.value,
      );
      const enabledGetPermissions = enableGetPermissionsEndpointSelector(
        _state$.value,
      );

      const { company, group, extraActions } = getInitialCompanyAndGroup();
      const token = getToken();

      return [
        ...extraActions,
        getAllPaymentMethodsV2(company, group),
        enabledGetUserProfiles
          ? getCompanyUserProfilesAction(token)
          : fetchUserInfoAction(token), // company info
        enabledGetPermissions
          ? getCompanyPermissionsAction(token)
          : fetchUserAuthorizationAction({ token, companyId: company.id }),
        fetchCityOptions(company.countryCode),
      ];
    }),
  );

const firstFetchServiceTypesEpic = (action$, state$) =>
  action$.pipe(
    ofType(FETCH_CITY.SUCCESS),
    mergeMap(_action => {
      // when user just logged-in -> fetch userInfo -> fire FETCH_USER_INFO.SUCCESS but localStorage still does sync with new token
      if (isAuthHintEmpty()) {
        return EMPTY;
      }
      const cities = _get(state$, 'value.app.cities', []);
      let userInfo = userInfoSelector(state$.value);

      if (!isPaxUser()) {
        const tempUserProfile = getTempSavedUserProfile();
        clearTempUserProfile();
        userInfo = {
          // contains current company information
          ...userInfoSelector(state$.value),
          // contains user's all companies
          companies: tempUserProfile.companies,
        };
      }

      if (
        _isEmpty(userInfo) ||
        // Pax user might not have linked gfb companies
        (!isPaxUser() && _isEmpty(userInfo.companies)) ||
        _isEmpty(cities)
      ) {
        return EMPTY;
      }
      let defaultCity;
      // For pax users who are using personal payments, use pax city logic
      // For all other cases (GFB users or pax users using enterprise profiles), use corporate city logic
      if (isPaxUser() && isUsingPaxPersonalPayments()) {
        defaultCity = getDefaultCityForPaxUserAccount(userInfo, cities);
      } else {
        // This handles:
        // 1. GFB users (non-pax users)
        // 2. Pax users who have switched to an enterprise profile
        defaultCity = getDefaultCityForCorpAccount(userInfo, cities);
      }

      if (!defaultCity) {
        return of(
          pushErrorModalAction({
            message: I18n.t('v2.error.home.noValidCities.title'),
            description: I18n.t('v2.error.home.noValidCities.description'),
            type: ERROR_MODAL_TYPE.WARNING,
            onOk: () => logOut(),
          }),
        );
      }

      const {
        companyID,
        countryCode,
        cityPOIToSearch,
        cityName,
        latlng,
      } = getInitAddressSearchParams(userInfo, defaultCity);

      const addressSearch$ = doAddressSearch(
        cityPOIToSearch,
        companyID,
        countryCode,
        latlng,
      );
      return forkJoin(
        addressSearch$,
        of(cityName),
        of(cities),
        of(countryCode),
        of(companyID),
      );
    }),
    switchMap(([{ response }, cityName, cities, countryCode, companyID]) => {
      const locations = _get(response, 'resp.result', []);
      const cityToStorage = extractCityFromSearchResults(
        locations,
        cities,
        cityName,
        countryCode,
      );
      saveCityStorage(cityToStorage);
      const getUserNameInfoSuccessAction = {
        type: GET_USERNAME_INFO.SUCCESS,
        data: {
          selectedCity: cityToStorage,
        },
      };

      return isPaxUser()
        ? of(
            getUserNameInfoSuccessAction,
            paxFetchServiceTypesAction(cityToStorage.cityID),
          )
        : of(
            getUserNameInfoSuccessAction,
            fetchServiceTypesActionV2(cityToStorage.cityID, companyID),
          );
    }),
  );

const checkPreConditionPaxLinkErrorEpic = (action$, state$) =>
  action$.pipe(
    ofType(FETCH_CITY.ERROR),
    switchMap(action => {
      const isFailedToVerifyPrecondition = _get(
        state$,
        'value.login.isFailedToVerifyPrecondition',
        false,
      );
      if (isFailedToVerifyPrecondition) {
        return of(
          createPaxLinkPreConditionErrorModal(
            ERROR_MODAL_TYPE.WARNING,
            'checkPreConditionPaxLinkErrorEpic:FETCH_CITY.ERROR',
          ),
        );
      }
      return EMPTY;
    }),
  );

const paxSetCompanyPaymentsEpic = (action$, state$) =>
  action$.pipe(
    ofType(PAX_FETCH_BUSINESS_PROFILES.SUCCESS),
    switchMap(action => {
      /*
       * If using pax personal payment profile:
       * 1) clear current company and group
       * 2) no following actions
      */
      if (isUsingPaxPersonalPayments()) {
        resetCompanyAndGroup();
        return EMPTY;
      }

      /*
       * If using pax company payment profile:
       * 1) get & set current company & group
       * 2) set payments to current company payments
      */
      let actions = [];
      const { extraActions } = getInitialCompanyAndGroup(
        _get(action, 'data.response.companies', []),
      );
      actions = [...extraActions];
      return actions;
    }),
  );

const prevalidateGFBLoginEpic = (action$, state$) =>
  action$.pipe(
    ofType(GET_USERNAME_INFO.SUCCESS),
    switchMap(_action => {
      // Skip this check if login with PAX account
      if (isPaxUser()) {
        return EMPTY;
      }

      const userInfo = userInfoSelector(state$.value);
      const isConnected = _get(userInfo, 'user.isConnected', false);

      if (!isConnected) {
        return of(
          createPaxLinkPreConditionErrorModal(
            ERROR_MODAL_TYPE.FAILED,
            'prevalidateGFBLoginEpic:GET_USERNAME_INFO.SUCCESS',
          ),
        );
      }

      // User has connected PAX app - proceed with normal flow
      return EMPTY;
    }),
  );

const globalInitFailureEpic = action$ =>
  action$.pipe(
    ofType(
      // not checking
      // PAX_FETCH_BUSINESS_PROFILES.ERROR,
      // GET_USERNAME_INFO.ERROR,
      // PAX_GET_USER_AUTHORIZATION.ERROR,
      // FETCH_USER_CONFIGURATION.ERROR
      // as they are non blocking. Basic functionality still works
      FETCH_CITY.ERROR,
      GLOBAL_INIT_CLIENT_ERROR,
      FETCH_USER_INFO.ERROR,
      FETCH_SERVICE_TYPES_V2.ERROR,
      PAX_FETCH_SERVICE_TYPES.ERROR,
    ),
    switchMap(action =>
      of(
        pushErrorModalAction({
          message: I18n.t('v2.error.globalInitError.message'),
          description: I18n.t('v2.error.globalInitError.description'),
          okButtonText: I18n.t('v2.error.globalInitError.button'),
          type: ERROR_MODAL_TYPE.WARNING,
          onOk: () => {
            window.location.reload();
          },
        }),
      ),
    ),
  );

export default combineEpics(
  globalInitClientEpic,
  initGFBUserConfigsEpic,
  initGFBUserRemainingActionsEpic,
  prevalidateGFBLoginEpic,
  globalInitFailureEpic,
  firstFetchServiceTypesEpic,
  checkPreConditionPaxLinkErrorEpic,
  paxSetCompanyPaymentsEpic,
);
